import React from 'react';
import { motion } from 'framer-motion';
import { BeakerIcon, ArrowTrendingUpIcon, SparklesIcon, ArrowsPointingOutIcon, ShieldCheckIcon, CalendarDaysIcon, GlobeEuropeAfricaIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Hydroponics = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const hydroponicsFeatures = [
        "Up to 90% water saving",
        "Faster growth cycles",
        "Higher nutrient efficiency",
        "Cleaner and residue-controlled produce",
        "Ideal for greenhouse & urban farming"
    ];

    const aeroponicsFeatures = [
        "Maximum oxygen exposure",
        "Faster root development",
        "Accelerated crop growth",
        "Higher space efficiency",
        "Ultra-precise nutrient control"
    ];

    const whyChooseSystems = [
        { title: "Year-round production", icon: CalendarDaysIcon },
        { title: "Reduced pest and disease risk", icon: ShieldCheckIcon },
        { title: "Better space utilization", icon: ArrowsPointingOutIcon },
        { title: "Predictable output", icon: ArrowTrendingUpIcon },
        { title: "Export-quality produce", icon: GlobeEuropeAfricaIcon }
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/newimages/mas website hydroponics.webp')}
                        alt="Hydroponics & Aeroponics Systems"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Hydroponics & <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Aeroponics Systems</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Soil-Free Farming. Future-Ready Production.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeInUp}
                    className="mb-16 text-center max-w-4xl mx-auto"
                >
                    <p className="text-gray-600 text-lg md:text-xl leading-relaxed mb-6 font-light">
                        <strong className="text-gray-900 font-bold">Hydroponics and Aeroponics</strong> at Marvelous Agri Solution represent the next evolution of high-efficiency agriculture.
                    </p>
                    <p className="text-gray-600 text-lg md:text-xl leading-relaxed font-light mb-8">
                        We design advanced soil-less cultivation systems that deliver faster growth, higher yield, and superior crop quality — using precision nutrient delivery and controlled environments.
                    </p>
                    <div className="inline-block bg-emerald-50 border border-emerald-200 text-emerald-800 px-6 py-3 rounded-full font-medium italic text-lg shadow-sm">
                        This is agriculture engineered for maximum performance.
                    </div>
                </motion.div>

                {/* Hydroponics & Aeroponics Systems Details */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">

                    {/* Hydroponics Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100 flex flex-col h-full hover:shadow-2xl hover:border-emerald-200 transition-all"
                    >
                        <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                            <BeakerIcon className="w-10 h-10 text-emerald-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                            Hydroponics System
                        </h3>
                        <p className="text-emerald-600 font-medium mb-6">Nutrient-Rich Water-Based Cultivation</p>

                        <p className="text-gray-600 leading-relaxed font-light mb-8">
                            Plants grow in a precisely balanced nutrient solution without soil.
                        </p>

                        <div className="space-y-4 mb-8 flex-grow">
                            {hydroponicsFeatures.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium">{item}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-auto bg-gray-50 p-6 rounded-2xl border-l-4 border-emerald-500">
                            <p className="font-bold text-gray-900">Every root receives accurate nutrition.</p>
                            <p className="font-bold text-gray-900">No soil limitations.</p>
                            <p className="font-bold text-gray-900">No nutrient loss.</p>
                        </div>
                    </motion.div>

                    {/* Aeroponics Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100 flex flex-col h-full hover:shadow-2xl hover:emerald-200 transition-all"
                    >
                        <div className="bg-emerald-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                            <SparklesIcon className="w-10 h-10 text-emerald-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                            Aeroponics System
                        </h3>
                        <p className="text-emerald-600 font-medium mb-6">Advanced Mist-Based Root Nutrition</p>

                        <p className="text-gray-600 leading-relaxed font-light mb-8">
                            In aeroponics, plant roots are suspended in air and nourished through nutrient-rich mist.
                        </p>

                        <div className="space-y-4 mb-8 flex-grow">
                            {aeroponicsFeatures.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-3">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium">{item}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-auto bg-gray-50 p-6 rounded-2xl border-l-4 border-emerald-500">
                            <p className="font-medium text-emerald-800 italic">
                                It is one of the most advanced cultivation systems in modern agriculture.
                            </p>
                        </div>
                    </motion.div>

                </div>

                {/* Why Choose Soil-Less Systems */}
                <div className="flex flex-col lg:flex-row gap-12 items-center">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-1/2"
                    >
                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            Why Choose Soil-Less Systems?
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                            {whyChooseSystems.map((item, idx) => (
                                <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                                    <div className="bg-emerald-50 p-2 rounded-lg">
                                        <item.icon className="w-6 h-6 text-emerald-600" />
                                    </div>
                                    <span className="font-medium text-gray-800">{item.title}</span>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <img src={require('../../../assets/newimages/mas websiote hydroponics.webp')} className="rounded-2xl w-full h-48 object-cover shadow-lg" alt="Hydroponics System" />
                            <img src={require('../../../assets/newimages/mas website hydroponic 2.webp')} className="rounded-2xl w-full h-48 object-cover shadow-lg translate-y-6" alt="Aeroponics System" />
                        </div>
                    </motion.div>

                    <div className="lg:w-1/2 w-full mt-10 lg:mt-0">
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-gray-900 p-10 md:p-14 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                        >
                            <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl text-white pointer-events-none"></div>

                            <h3 className="text-3xl font-bold mb-8 relative z-10 text-emerald-400">
                                Farming Beyond Traditional Boundaries
                            </h3>

                            <div className="relative z-10 space-y-6">
                                <p className="text-xl font-light text-gray-300 leading-relaxed">
                                    <span className="text-white font-medium">Hydroponics</span> and <span className="text-white font-medium">Aeroponics</span> eliminate dependency on soil quality and climate uncertainty.
                                </p>
                                <div className="w-16 h-1 bg-emerald-500 rounded-full"></div>
                                <p className="text-xl font-light text-gray-300 leading-relaxed">
                                    At <strong className="text-white font-bold">Marvelous Agri Solution</strong>, we design scalable, intelligent systems that deliver consistent performance, sustainability, and profitability.
                                </p>
                            </div>
                        </motion.div>
                    </div>

                </div>
            </section>
        </div>
    );
};

export default Hydroponics;
